


































































import Vue from "vue";
import { v4 as uuidv4 } from "uuid";
import { setCookie, getCookie } from "../scripts/helpers/cookie/cookie";
import { mixin as clickaway } from "vue-clickaway";

export default Vue.extend({
    mixins: [clickaway],

    props: {
        isVisible: {
            type: Boolean,
            required: false,
            default: false
        },
        tooltipOffset: {
            type: Number,
            required: false,
            default: 20
        },
        tooltipBorderOffset: {
            type: Number,
            required: false,
            default: 2
        },
        tooltipCaretOffset: {
            type: Number,
            required: false,
            default: 35
        },
        targetElementId: {
            type: String,
            required: true
        },
        stickyElementId: {
            type: String,
            required: false,
            default: ""
        },
        position: {
            type: String,
            required: false,
            default: "right"
        },
        align: {
            type: String,
            required: false,
            default: "center"
        },
        fixed: {
            type: Boolean,
            required: false,
            default: false
        },
        sticky: {
            type: Boolean,
            required: false,
            default: false
        },
        isStandardEventListener: {
            type: Boolean,
            required: false,
            default: true
        },
        displayEventType: {
            type: String,
            required: false,
            default: "click"
        },
        displayEventListener: {
            type: String,
            required: false,
            default: ""
        },
        displayDelay: {
            type: Number,
            required: false,
            default: 0
        },
        isCoachmarkBehaviour: {
            type: Boolean,
            required: false,
            default: false,
        },
        coachmarkId: {
            type: String,
            required: false,
            default: ""
        },
        isCoachmarkButton: {
            type: Boolean,
            required: false,
            default: false
        },
        coachmarkButtonLabel: {
            type: String,
            required: false,
            default: ""
        },
        coachmarkCookieExpires: {
            type: Number,
            required: false,
            default: 0
        },
        tooltipText: {
            type: String,
            required: false,
            default: ""
        },
        isClickOutsideBlocked: {
            type: Boolean,
            required: false,
            default: false
        },
        /**
         * Below this breakpoint, tooltip uses full-screen modal layout (touch-friendly).
         * "medium" matches max-width ~767px; "desktop" extends through tablet (~991px).
         */
        fullScreenUntil: {
            type: String,
            required: false,
            default: "medium",
            validator: function (value: string): boolean {
                return ["medium", "desktop"].indexOf(value) !== -1;
            }
        },
        /**
         * When true, never use the mobile full-screen modal; use the same positioned popover as desktop.
         */
        disableFullScreenLayout: {
            type: Boolean,
            required: false,
            default: false
        },
        /**
         * When set (e.g. "top"), uses this position instead of `position` below the `fullScreenUntil` breakpoint.
         */
        positionWhenMobile: {
            type: String,
            required: false,
            default: "",
            validator: function (value: string): boolean {
                if (!value) {
                    return true;
                }
                return ["top", "bottom", "left", "right"].indexOf(value) !== -1;
            }
        }
    },
    data: function () {
        return {
            uniqueId: uuidv4() as string,
            targetElementTop: 0 as number,
            targetElementBottom: 0 as number,
            targetElementLeft: 0 as number,
            targetElementRight: 0 as number,
            targetElementHeight: 0 as number,
            targetElementWidth: 0 as number,
            tooltipHeight: 0 as number,
            tooltipWidth: 0 as number,
            internalIsVisible: false as boolean,
            isMobile: false as boolean,
            isClickedClosedCross: false as boolean,
        };
    },
    computed: {

        isFullScreenLayout: function (): boolean {
            return (
                this.isMobile &&
                !this.isCoachmarkBehaviour &&
                !this.disableFullScreenLayout
            );
        },

        classObject: function (): Record<string, boolean> {
            return {
                "visible": this.internalIsVisible,
                "tooltip--fixed": this.fixed,
                "tooltip--modal-like": this.isFullScreenLayout,
                "coachmark": this.isCoachmarkBehaviour,
            };
        },
        tooltipPhrases: function(): string[] {
            const text = this.tooltipText as string;
            return text.split(/\\n/) as string[];
        },
        effectivePosition: function (): string {
            if (this.isMobile && this.positionWhenMobile) {
                return this.positionWhenMobile;
            }
            return this.position;
        },
        tooltipId: function (): string {
            let tooltipId = "" as string;
            if (this.sticky) {
                tooltipId = "tooltip-sticky-" + this.targetElementId;
            } else if (this.isCoachmarkBehaviour) {
                tooltipId = this.coachmarkId;
            } else {
                tooltipId = "tooltip-" + this.uniqueId;
            }

            return tooltipId;
        },
        /**
         * Inline position + optional caret anchor (mobile top tooltips) for `::after`.
         */
        tooltipLayoutStyle: function (): Record<string, string> | undefined {
            if (this.isFullScreenLayout && !this.fixed) {
                return undefined;
            }

            const pos = this.computeTooltipPositionPixels();
            const style: Record<string, string> = {
                top: `${Math.round(pos.top)}px`,
                left: `${Math.round(pos.left)}px`,
            };

            if (
                this.isMobile &&
                this.positionWhenMobile &&
                this.effectivePosition === "top" &&
                this.caretAlignment === "center"
            ) {
                const iconCenter = this.targetElementLeft + this.targetElementWidth / 2;
                const caretCenterPx = iconCenter - pos.left;
                style["--tooltip-caret-left"] = `${Math.round(caretCenterPx)}px`;
            }

            return style;
        },
        /**
         * [caretPosition The 'main' position of the caret (triangle), define on which side of the tooltip it's display]
         * @return {String} [The class used to display the tooltip caret]
         */
        caretPosition: function (): string {
            if (this.isFullScreenLayout) { return ""; }

            let caretClass;

            switch (this.effectivePosition) {
            case "left":
                caretClass = `tooltip--caret-right-${this.caretAlignment}`;
                break;
            case "right":
                caretClass = `tooltip--caret-left-${this.caretAlignment}`;
                break;
            case "top":
                caretClass = `tooltip--caret-bottom-${this.caretAlignment}`;
                break;
            case "bottom":
                caretClass = `tooltip--caret-top-${this.caretAlignment}`;
                break;
            default:
                caretClass = `tooltip--caret-left-${this.caretAlignment}`;
            }

            return caretClass;
        },
        /**
         * [caretAlignment The alignment of the caret (triangle), could be center/left/right or center/top/bottom based on the tooltip position]
         * @return {String} [The alignment, used by caretPosition to construct the class]
         */
        caretAlignment: function (): string {
            let alignment = "center";

            switch (this.align) {
            case "left":
            case "right":
                if (this.effectivePosition === "top" || this.effectivePosition === "bottom") {
                    alignment = this.align;
                } else {
                    alignment = "center";
                }
                break;
            case "top":
            case "bottom":
                if (this.effectivePosition === "left" || this.effectivePosition === "right") {
                    alignment = this.align;
                } else {
                    alignment = "center";
                }
                break;
            default:
                alignment = "center";
            }

            return alignment;
        }
    },
    mounted: function () {
        const fullScreenMediaQuery =
            this.fullScreenUntil === "desktop" ? "to-desktop" : "to-medium";

        this.$responsive.registerMediaQuery(fullScreenMediaQuery, () => {
            this.isMobile = true;
        }, () => {
            this.isMobile = false;
        });

        this.constructTooltip();

        Vue.nextTick(() => {
            window.addEventListener("resize", this.handleResizeScreen);
            window.addEventListener("click", this.refreshPosition);

            this.handleResizeScreen();

            if (this.isCoachmarkBehaviour) {
                let coachmarkCookie = getCookie("coachmark", true) as Record<string, unknown>;
                if (coachmarkCookie && coachmarkCookie[this.coachmarkId] === true) {
                    this.internalIsVisible = false;
                } else {
                    if (this.displayDelay > 0) {
                        setTimeout(() => {
                            this.handleTooltipDisplay();
                        }, this.displayDelay);
                    }
                }
            } else {
                // Add event following displayType (click, mouseover, ...) to show the tooltip
                const targetElement = document.getElementById(this.targetElementId) as HTMLElement;
                if (targetElement !== null) {
                    if (this.isStandardEventListener && this.displayEventType !== "") {
                        targetElement.addEventListener(this.displayEventType, this.handleTooltipDisplay);
                    } else {
                        eval(this.displayEventListener);
                    }
                }
            }
        });
    },
    beforeDestroy: function(){
        // Remove tooltip dom element
        Vue.nextTick(() => {
            let tooltipContainer = document.getElementById("tooltip-group") as HTMLElement;
            if(this.sticky){
                let targetElement = document.getElementById(this.targetElementId) as HTMLElement;
                if(targetElement) {
                    if (this.isCoachmarkBehaviour) {
                        tooltipContainer = document.getElementById(this.tooltipId) as HTMLElement;
                    } else {
                        tooltipContainer = targetElement.parentElement as HTMLElement;
                    }
                }
            }

            if (tooltipContainer) {
                const tooltip = document.getElementById(this.tooltipId) as HTMLElement;
                if (tooltip) {
                    // Some browsers should use remove when other browser (IE 11 / Edge and, probably older safari are still using removeChild)
                    if (tooltip.remove) {
                        tooltip.remove();
                    } else {
                        tooltipContainer.removeChild(tooltip);
                    }
                }
            }
        });
    },
    methods: {
        /**
         * Tooltip box position in the same coordinate system as existing logic.
         */
        computeTooltipPositionPixels: function (): { top: number; left: number } {
            let top = 0;
            let left = 0;

            switch (this.effectivePosition) {
            case "top":
                top = (this.targetElementTop - this.tooltipHeight) - this.tooltipOffset;
                break;
            case "bottom":
                top = this.targetElementBottom + this.tooltipOffset;
                break;
            case "left":
            case "right":
            default:
                if (this.caretAlignment === "top") {
                    top = this.targetElementTop - (this.targetElementHeight / 2) + (3 * this.tooltipBorderOffset);
                } else if (this.caretAlignment === "bottom") {
                    top = this.targetElementBottom - this.tooltipHeight + (this.targetElementHeight / 2) - (3 * this.tooltipBorderOffset);
                } else {
                    top = this.targetElementBottom - (this.targetElementHeight / 2) - (this.tooltipHeight / 2);
                }
            }

            if (this.caretAlignment === "right") {
                left = this.targetElementLeft - this.tooltipWidth + (this.targetElementWidth / 2) + (this.tooltipCaretOffset / 2) + this.tooltipBorderOffset;
            } else if (this.caretAlignment === "left") {
                left = this.targetElementLeft + (this.targetElementWidth / 2) - (this.tooltipCaretOffset / 2) + (this.tooltipBorderOffset / 2);
            } else {
                switch (this.effectivePosition) {
                case "left":
                    left = this.targetElementLeft - this.tooltipWidth - this.tooltipOffset + this.tooltipBorderOffset;
                    break;
                case "top":
                case "bottom":
                    left = (this.targetElementLeft + (this.targetElementWidth / 2)) - (this.tooltipWidth / 2) + this.tooltipBorderOffset;
                    break;
                case "right":
                default:
                    left = this.targetElementLeft + this.targetElementWidth + this.tooltipOffset + this.tooltipBorderOffset;
                }
            }

            if (this.isMobile && this.positionWhenMobile && typeof window !== "undefined") {
                const horizontalInset = 16;
                const vw = window.innerWidth;
                if (this.tooltipWidth > 0) {
                    left = Math.max(
                        horizontalInset,
                        Math.min(left, vw - this.tooltipWidth - horizontalInset)
                    );
                } else {
                    left = Math.max(horizontalInset, left);
                }
            }

            return { top: top, left: left };
        },

        handleClickAway: function (event: PointerEvent): void {
            if (this.isClickOutsideBlocked) {
                return;
            }

            const activeElement = document.activeElement as HTMLElement | null;
            const activeId = activeElement ? activeElement.id : null;

            if (activeId !== this.targetElementId && this.internalIsVisible) {
                this.handleTooltipDismiss();
            }
        },

        constructTooltip: function () {

            // Move the tooltip, not the coachmark :
            // - to the tooltipGroup if not sticky tooltip
            // - to the parent element if sticky tooltip

            if (this.isCoachmarkBehaviour) {
                return false;
            }

            const tooltip = this.$el;

            let targetElement = document.getElementById(this.targetElementId) as HTMLElement;
            let tooltipContainer = document.getElementById("tooltip-group") as HTMLElement;
            let tooltipSticky = document.getElementById("tooltip-sticky-" + this.targetElementId) as HTMLElement;

            if (this.sticky) {
                if (targetElement) {
                    tooltipContainer = targetElement.parentElement as HTMLElement;
                }
            } else {
                // Remove sticky tooltip from parent element because sticky value changed
                if (tooltipSticky) {
                    // Some browsers should use remove when other browser (IE 11 / Edge and, probably older safari are still using removeChild)
                    if (tooltipSticky.remove) {
                        tooltipSticky.remove();
                    } else {
                        tooltipContainer.removeChild(tooltipSticky);
                    }
                }
                // If there is no tooltipContainer create one
                if (!tooltipContainer) {
                    tooltipContainer = document.createElement("div");
                    tooltipContainer.id = "tooltip-group";
                    document.body.appendChild(tooltipContainer);
                }
            }

            let tooltipAlreadyExists = false as boolean;
            for (let index = 0; index < tooltipContainer.children.length; index++) {
                if (tooltipContainer.children[index].id === tooltip.id) {
                    tooltipAlreadyExists = true;
                    index = tooltipContainer.children.length + 1;
                }
            }

            if (!tooltipAlreadyExists) {
                tooltipContainer.appendChild(tooltip);
            }
        },
        handleResizeScreen: function () {
            setTimeout(
                () => {
                    this.constructTooltip();
                    this.refreshPosition();
                },
                150
            );
        },
        refreshPosition: function () {
            Vue.nextTick(() => {
                // Dirty setTimeout of 0 sec (To make sure all element are correctly position in the dom)
                let timeOut = 0;

                setTimeout(
                    () => {
                        const targetElement = document.getElementById(this.targetElementId) as HTMLElement;
                        let stickyTopValue = 0;
                        let stickyLeftValue = 0;

                        if (targetElement !== null) {
                            const scrollTop = document.documentElement.scrollTop ? document.documentElement.scrollTop : document.body.scrollTop;
                            const targetElementRect = targetElement.getBoundingClientRect();

                            let scrollTopValue = (this.sticky || this.fixed) ? 0 : scrollTop;

                            this.targetElementHeight = Math.round(targetElement.offsetHeight);
                            this.targetElementWidth = Math.round(targetElement.offsetWidth);

                            if (this.sticky) {
                                const stickyElementContainer = document.getElementById(this.stickyElementId) as HTMLElement;
                                const stickyElementContainerRect = stickyElementContainer.getBoundingClientRect();
                                stickyTopValue = Math.round(stickyElementContainerRect.top);
                                stickyLeftValue = Math.round(stickyElementContainerRect.left);
                            }


                            this.targetElementTop = Math.round(targetElementRect.top) - stickyTopValue + scrollTopValue;
                            this.targetElementBottom = Math.round(targetElementRect.bottom) - stickyTopValue + scrollTopValue;
                            this.targetElementLeft = Math.round(targetElementRect.left) - stickyLeftValue + this.tooltipBorderOffset;
                            this.targetElementRight = Math.round(targetElementRect.right) - stickyLeftValue + this.tooltipBorderOffset;
                        }

                        const element = this.$el as HTMLElement;
                        if (element !== null) {
                            let elementRect = element.getBoundingClientRect();
                            let elementTop = Math.round(elementRect.top);
                            let coachmarkButtonHeight = 0;
                            if (this.isCoachmarkButton && elementTop === stickyTopValue) {
                                // on first load of coachmark, the height of button is not calculated
                                let tooltipContent = element.lastElementChild;
                                if (tooltipContent) {
                                    let coachmarkButton = tooltipContent.lastElementChild;
                                    if (coachmarkButton) {
                                        let coachmarkButtonRect = coachmarkButton.getBoundingClientRect();
                                        coachmarkButtonHeight = Math.round(coachmarkButtonRect.height);
                                    }
                                }
                            }
                            this.tooltipHeight = Math.round(element.offsetHeight) + (coachmarkButtonHeight / 2);
                            this.tooltipWidth = Math.round(element.offsetWidth);

                            /**
                             * The condition is here to prevent coachmarks
                             * to be focused when the screen resize.
                             */
                            if (!this.isCoachmarkBehaviour) {
                                if (!this.isClickedClosedCross) {
                                    element.focus();
                                } else {
                                    targetElement.focus();
                                    this.isClickedClosedCross = false;
                                }
                            }
                        }
                    },
                    timeOut
                );
            });
        },
        handleTooltipDismiss: function () {
            document.documentElement.classList.remove("js-fullscreen");
            this.internalIsVisible = false;
            this.isClickedClosedCross = true;
            if (this.isCoachmarkBehaviour) {
                this.handleCoachmarkDismiss();
            }
        },
        handleTooltipDisplay: function () {
            this.internalIsVisible = true;
            if (this.isFullScreenLayout) {
                document.documentElement.classList.add("js-fullscreen");
            }
        },
        handleCoachmarkDismiss: function () {
            let coachmarkCookie = getCookie("coachmark", true) as Record<string, unknown>;

            if (!coachmarkCookie) {
                coachmarkCookie = {};
            }

            coachmarkCookie[this.coachmarkId] = true;

            let isCookieSecure = true;
            if (process.env.VUE_APP_ENVIRONMENT_NAME === "local") {
                isCookieSecure = false;
            }

            setCookie("coachmark", coachmarkCookie, { "path": "/", "domain": process.env.VUE_APP_ROOT_URL, "httponly": false, "expires": this.coachmarkCookieExpires, "secure": isCookieSecure });
        },
    }
});
